/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useEffect, useState } from 'react';

import { cssMap } from '@compiled/react';
import { bind } from 'bind-event-listener';
import { useIntl } from 'react-intl-next';

import { css, jsx } from '@atlaskit/css';
import StatusSuccessIcon from '@atlaskit/icon/core/status-success';
import { Inline, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import VisuallyHidden from '@atlaskit/visually-hidden';

import { AI_MODAL_SCREEN_CLASS } from '../../../utils/constants';
import { FloatingContainer } from '../../components/FloatingContainer/FloatingContainer';

import messages from './messages';

type Props = {
	isHidden: boolean;
	onceFade: () => void;
};

const noImprovementFloatingContainerStyle = css({
	/**
	 * Scroll margin is used to ensure that the modal won't be
	 * too close to the top of the editor, which may sometimes
	 * cover the modal slightly.
	 */
	scrollMarginTop: token('space.400'),
	justifySelf: 'center',
	transition: 'opacity 1s ease-in-out',
	width: 'fit-content',
});

const fadeOutStyle = css({
	opacity: 0,
});

const noImprovementStyles = cssMap({
	container: {
		display: 'flex',
		flexDirection: 'row',
		gap: token('space.100'),
		paddingRight: token('space.050'),
		paddingLeft: token('space.050'),
	},
	content: {
		display: 'flex',
		flexDirection: 'row',
		justifyContent: 'flex-start',
		alignItems: 'center',
		flex: 1,
		gap: token('space.100'),
		paddingTop: token('space.075'),
		paddingRight: token('space.050'),
		paddingBottom: token('space.075'),
		paddingLeft: token('space.050'),
	},
});

export const NoImprovementScreen = ({ isHidden, onceFade }: Props) => {
	const { formatMessage } = useIntl();
	const [containerElement, setContainerElement] = useState<HTMLDivElement | null>(null);

	const setRootContainer = (element: HTMLDivElement | null) => {
		setContainerElement(element);
	};

	// Scroll the no improvement screen into view if it's not fully visible
	useEffect(() => {
		if (containerElement) {
			containerElement.scrollIntoView({ block: 'nearest' });
		}
	}, [containerElement]);

	// Handle fade animation completion
	useEffect(() => {
		if (isHidden && onceFade && containerElement) {
			const handleTransitionEnd = (event: TransitionEvent) => {
				if (event.propertyName === 'opacity') {
					onceFade();
				}
			};

			const unbind = bind(containerElement, {
				type: 'transitionend',
				listener: handleTransitionEnd,
			});

			return unbind;
		}
	}, [isHidden, onceFade, containerElement]);

	return (
		<FloatingContainer
			ref={setRootContainer}
			data-testid="no-improvement-screen"
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
			className={AI_MODAL_SCREEN_CLASS}
			rainbowBorder={false}
			css={[noImprovementFloatingContainerStyle, isHidden && fadeOutStyle]}
		>
			<Inline xcss={noImprovementStyles.container}>
				<VisuallyHidden>{formatMessage(messages.successIconLabel)}</VisuallyHidden>
				<Inline xcss={noImprovementStyles.content}>
					<Text>
						<StatusSuccessIcon
							label={formatMessage(messages.successIconLabel)}
							color={token('color.icon.success')}
						/>
					</Text>
					<Text>{formatMessage(messages.noImprovementMessage)}</Text>
				</Inline>
			</Inline>
		</FloatingContainer>
	);
};
